import React from "react";
import { Box, useTheme, keyframes } from "@mui/material";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const splatter = keyframes`
  from { opacity: 0; transform: scale(0); }
  to { opacity: 1; transform: scale(1); }
`;

const getColorPalette = (mode) => {
    if (mode === "dark") {
        return [
            "rgba(138, 43, 226, 0.4)",   // Blue Violet
            "rgba(70, 130, 180, 0.4)",   // Steel Blue
            "rgba(72, 209, 204, 0.4)",   // Medium Turquoise
            "rgba(255, 160, 122, 0.4)",  // Light Salmon
            "rgba(255, 105, 180, 0.4)",  // Hot Pink
        ];
    } else {
        return [
            "rgba(255, 182, 193, 0.5)", // Light Pink
            "rgba(173, 216, 230, 0.5)", // Light Blue
            "rgba(144, 238, 144, 0.5)", // Light Green
            "rgba(255, 255, 224, 0.5)", // Light Yellow
            "rgba(255, 228, 196, 0.5)", // Light Peach
        ];
    }
};

const generateRandomShapes = (num, colors) =>
    Array.from({ length: num }, (_, i) => {
        const size = Math.random() * 200 + 20;
        return {
            id: `shape-${i}`,
            width: size,
            height: size,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            finalOpacity: Math.random() * 0.5 + 0.3,
            animationDuration: `${Math.random() * 2 + 1}s`,
            animationDelay: `${Math.random() * 2}s`,
        };
    });

const generateSplatterShapes = (num, colors, direction = "leftToRight") => {
    const startPos = direction === "leftToRight"
        ? { top: Math.random() * 100, left: 0 }
        : { top: Math.random() * 100, left: 100 };
    const endPos = direction === "leftToRight"
        ? { top: Math.random() * 100, left: 100 }
        : { top: Math.random() * 100, left: 0 };

    const cp1 = { top: Math.random() * 100, left: 50 };
    const cp2 = { top: Math.random() * 100, left: 50 };

    const color = colors[Math.floor(Math.random() * colors.length)];

    return Array.from({ length: num }, (_, i) => {
        const t = i / num;
        const bezier = (a, b, c, d) =>
            (1 - t) ** 3 * a +
            3 * (1 - t) ** 2 * t * b +
            3 * (1 - t) * t ** 2 * c +
            t ** 3 * d +
            Math.random() * 10 - 5;

        const size = Math.random() * 20 + 10;
        return {
            id: `splatter-${direction}-${i}`,
            width: size,
            height: size,
            top: `${bezier(startPos.top, cp1.top, cp2.top, endPos.top)}%`,
            left: `${bezier(startPos.left, cp1.left, cp2.left, endPos.left)}%`,
            backgroundColor: color,
            finalOpacity: Math.random() * 0.5 + 0.5,
            animationDuration: `${Math.random() * 1 + 0.5}s`,
            animationDelay: `${Math.random()}s`,
        };
    });
};

const StaticBackground = ({ numShapes = 12, numSplatterShapes = 50 }) => {
    const theme = useTheme();
    const colors = getColorPalette(theme.palette.mode);

    const shapes = generateRandomShapes(numShapes, colors);
    const splatters1 = generateSplatterShapes(numSplatterShapes, colors, "leftToRight");
    const splatters2 = generateSplatterShapes(numSplatterShapes, colors, "rightToLeft");

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                overflow: "hidden",
                zIndex: -2,
            }}
        >
            {[...shapes, ...splatters1, ...splatters2].map((shape) => (
                <Box
                    key={shape.id}
                    sx={{
                        position: "absolute",
                        borderRadius: "50%",
                        width: shape.width,
                        height: shape.height,
                        top: shape.top,
                        left: shape.left,
                        backgroundColor: shape.backgroundColor,
                        opacity: 0,
                        animation: `${shape.id.includes("splatter") ? splatter : fadeIn} ${shape.animationDuration
                            } ease-in ${shape.animationDelay}`,
                        animationFillMode: "forwards",
                    }}
                />
            ))}
        </Box>
    );
};

export default StaticBackground;
